/**
 * Multi-provider usage badge in the top nav.
 *
 * Polls /api/openrouter-usage and /api/claude-usage in parallel and renders a
 * compact logo + ring for every provider whose probe came back ``ok``:
 *
 *   • OpenRouter — window spend % against the key's cap (or the raw all-time
 *     spend when the key is uncapped, since there's no % to plot).
 *   • Anthropic  — the binding (worst-case) rate-limit utilisation across the
 *     5h / 7d / overage windows.
 *
 * Each endpoint self-gates: it returns ``unavailable`` (without probing
 * upstream) when its key/credential isn't configured, so the badge naturally
 * shows one provider, both, or — when neither is set up — hides entirely.
 * Clicking opens the UsageDetailPanel popover with the full per-provider
 * breakdown. Ring colour tracks severity: teal safe, amber past the warning
 * threshold, pink-red near the cap (see usageShared.tsx).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "@/api/client";
import { AnthropicLogo, OpenRouterLogo } from "@/components/common/ProviderLogos";
import { UsageDetailPanel } from "./UsageDetailPanel";
import type { ClaudeUsageResponse, OpenRouterUsageResponse } from "./usageShared";
import {
  MIN_SPIN_MS,
  POLL_MS,
  ProgressRing,
  claudeHeadlinePct,
  fmtUsd,
} from "./usageShared";

interface UsageState {
  openrouter: OpenRouterUsageResponse | null;
  claude: ClaudeUsageResponse | null;
}

const EMPTY: UsageState = { openrouter: null, claude: null };

/** Compact OpenRouter unit: logo + spend ring (or raw $ when uncapped). */
function OpenRouterMini({ data }: { data: OpenRouterUsageResponse }) {
  const capped = data.limit_usd != null && data.limit_usd > 0 && data.usage_pct != null;
  const title = capped
    ? `OpenRouter — ${fmtUsd(data.usage_window_usd ?? data.usage_usd)} of ${fmtUsd(data.limit_usd)} (${data.usage_pct}%)`
    : `OpenRouter — ${fmtUsd(data.usage_usd)} spent (no key limit set)`;
  return (
    <span className="flex items-center gap-1.5" title={title}>
      <OpenRouterLogo className="w-3.5 h-3.5 text-space-50" />
      {capped ? (
        <ProgressRing pct={data.usage_pct as number} />
      ) : (
        <span className="text-[12px] font-semibold tabular-nums text-space">{fmtUsd(data.usage_usd)}</span>
      )}
    </span>
  );
}

/** Compact Anthropic unit: logo + binding-utilisation ring. */
function ClaudeMini({ data }: { data: ClaudeUsageResponse }) {
  const fh = data.five_hour?.utilization;
  const sd = data.seven_day?.utilization;
  const title =
    `Claude account usage —` +
    (fh != null ? ` 5h ${fh}%` : "") +
    (sd != null ? `${fh != null ? " ·" : ""} 7d ${sd}%` : "");
  return (
    <span className="flex items-center gap-1.5" title={title}>
      <AnthropicLogo className="w-3.5 h-3.5 text-space-50" />
      <ProgressRing pct={claudeHeadlinePct(data)} />
    </span>
  );
}

export function UsageBadge() {
  const [usage, setUsage] = useState<UsageState>(EMPTY);
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Fetch both providers together; each endpoint self-gates, so a provider
  // that isn't configured simply resolves to null and renders nothing.
  const fetchUsage = useCallback(async (force: boolean): Promise<UsageState> => {
    const qs = force ? "?force=true" : "";
    const [or, cl] = await Promise.all([
      apiFetch<OpenRouterUsageResponse>(`/openrouter-usage${qs}`).catch(() => null),
      apiFetch<ClaudeUsageResponse>(`/claude-usage${qs}`).catch(() => null),
    ]);
    return {
      openrouter: or && or.status === "ok" && or.usage_usd != null ? or : null,
      claude:
        cl && cl.status === "ok" && (cl.five_hour || cl.seven_day || cl.overage) ? cl : null,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const v = await fetchUsage(false);
      if (!cancelled) setUsage(v);
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [fetchUsage]);

  // Dismiss the popover on an outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    const start = Date.now();
    const v = await fetchUsage(true);
    setUsage(v);
    // Keep the spinner up for a beat so a sub-second refresh still reads as
    // a deliberate action rather than a flicker.
    const elapsed = Date.now() - start;
    window.setTimeout(() => setRefreshing(false), Math.max(0, MIN_SPIN_MS - elapsed));
  }, [fetchUsage, refreshing]);

  const { openrouter, claude } = usage;
  if (!openrouter && !claude) return null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Provider usage"
        title="View provider usage"
        className="flex items-center gap-2.5 px-2.5 py-1 bg-space-20 rounded-lg border border-space-30 hover:border-space-40 transition-colors"
      >
        {openrouter && <OpenRouterMini data={openrouter} />}
        {openrouter && claude && <span className="w-px h-6 bg-space-30" />}
        {claude && <ClaudeMini data={claude} />}
      </button>

      {open && (
        <UsageDetailPanel
          openrouter={openrouter}
          claude={claude}
          onClose={() => setOpen(false)}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />
      )}
    </div>
  );
}
